'use client';

import { useChat } from '@ai-sdk/react';
import { cjk } from '@streamdown/cjk';
import { Bot, Brain, Check, ChevronDown, Component, ExternalLink, Eye, MapPin, MessageCircle, Milestone, MousePointer2, Power, Salad, Search, type LucideIcon } from 'lucide-react';
import { DefaultChatTransport } from 'ai';
import { motion } from 'framer-motion';
import { type ComponentPropsWithoutRef, type KeyboardEvent, type ReactNode, isValidElement, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Streamdown, type Components } from 'streamdown';
import A2UIMessageRenderer, { InteractiveA2UIMessageRenderer } from '@/components/custom/chat/chat-panel/A2UIMessageRenderer';
import type { CanvasUserAction } from '@/lib/vo/chat';
import { chatMessageMetadataSchema, type ChatUIMessage } from './types';

const chatTransport = new DefaultChatTransport({ api: '/api/chat' });
const CHAT_INPUT_SELECTOR = 'textarea[data-testid="a2ui-chat-input"]';
const CHAT_SETTINGS_STORAGE_KEY = 'a2ui-chat-settings:v2';
const LEGACY_CHAT_SETTINGS_STORAGE_KEY = 'a2ui-chat-settings:v1';
const LEGACY_CHAT_MODEL_STORAGE_KEY = 'a2ui-chat-model';
const LEGACY_A2UI_MODEL_STORAGE_KEY = 'a2ui-chat-a2ui-model';
const LEGACY_REASONING_STORAGE_KEY = 'a2ui-chat-reasoning';
const LEGACY_INPUT_STORAGE_KEY = 'a2ui-chat-input';
const INPUT_HISTORY_LIMIT = 100;
const COMPOSER_MIN_HEIGHT = 40;
const COMPOSER_MAX_HEIGHT = 180;
const BOTTOM_STICK_THRESHOLD = 56;
const starterPrompts = [
  'Give me a healthy recipe and render it on the canvas',
  'Recommend a few travel destinations on the canvas',
  'Look up Apple’s latest quarterly report and lay it out on the canvas as a brief table',
  'Showcase as many canvas components as you can',
];
const starterPromptIcons = [Salad, MapPin, Search, Component] as const satisfies readonly LucideIcon[];
const chatModelOptions = [
  { value: 'gpt-5.5', label: 'GPT-5.5', shortLabel: '5.5' },
  { value: 'gpt-5.4', label: 'GPT-5.4', shortLabel: '5.4' },
  { value: 'gpt-5.4-mini', label: 'GPT-5.4 mini', shortLabel: '5.4-mini' },
] as const;
const reasoningOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'XHigh' },
] as const;
const a2uiModelOptions = [
  { value: 'macaron-a2ui-tall', label: 'Macaron-A2UI-Tall', shortLabel: 'Tall' },
  { value: 'macaron-a2ui-venti', label: 'Macaron-A2UI-Venti', shortLabel: 'Venti' },
] as const;
const codeLanguagePattern = /language-([^\s]+)/;
const morphSelectTransition = { type: 'spring', stiffness: 620, damping: 42, mass: 0.7 } as const;
const morphSelectContentTransition = { duration: 0.16, ease: [0.22, 1, 0.36, 1] } as const;
const morphSelectWidthTransition = { duration: 0.34, ease: [0.22, 1, 0.36, 1] } as const;
const morphSelectTriggerHeight = 36;
const morphSelectOptionHeight = 32;
const morphSelectPanelChromeHeight = 9;
const morphSelectViewportMargin = 12;
const morphSelectResponsiveStyle = `@container (max-width: 640px) { [data-chat-setting-icon] { display: block; } [data-chat-setting-label] { display: none; } } @container (max-width: 480px) { [data-chat-setting-value] { display: none; } [data-chat-setting-value-short] { display: inline; } }`;
type MorphSelectOpenDirection = 'down' | 'up';
type ChatModel = (typeof chatModelOptions)[number]['value'];
type ReasoningEffort = (typeof reasoningOptions)[number]['value'];
type A2UIModel = (typeof a2uiModelOptions)[number]['value'];
type MorphSelectOption<T extends string> = { value: T; label: string; shortLabel?: string };
type ChatSettings = { model: ChatModel; a2uiModel: A2UIModel; reasoning: ReasoningEffort; input: string; inputHistory: string[] };
const DEFAULT_CHAT_SETTINGS: ChatSettings = { model: 'gpt-5.5', a2uiModel: 'macaron-a2ui-venti', reasoning: 'low', input: '', inputHistory: [] };
const isChatModel = (value: string): value is ChatModel => chatModelOptions.some((option) => option.value === value);
const isA2UIModel = (value: string): value is A2UIModel => a2uiModelOptions.some((option) => option.value === value);
const isReasoningEffort = (value: string): value is ReasoningEffort => reasoningOptions.some((option) => option.value === value);

type ChatMessagePart = ChatUIMessage['parts'][number];
type ChatTextPart = Extract<ChatMessagePart, { type: 'text' }>;

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest("input, textarea, [contenteditable='true']"));
}

function focusChatInput() {
  const input = document.querySelector<HTMLTextAreaElement>(CHAT_INPUT_SELECTOR);
  if (!input || input.disabled) return;
  input.focus();
  const cursor = input.value.length;
  input.setSelectionRange(cursor, cursor);
}
function normalizeInputHistory(value: unknown): string[] | undefined {
  if (!Array.isArray(value) || !value.every((entry) => typeof entry === 'string')) return undefined;
  return value
    .map((entry) => entry.trim())
    .filter(Boolean)
    .slice(-INPUT_HISTORY_LIMIT);
}
function normalizeChatSettings(value: unknown): Partial<ChatSettings> {
  const record = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const inputHistory = normalizeInputHistory(record.inputHistory);
  return {
    ...(typeof record.model === 'string' && isChatModel(record.model) ? { model: record.model } : {}),
    ...(typeof record.a2uiModel === 'string' && isA2UIModel(record.a2uiModel) ? { a2uiModel: record.a2uiModel } : {}),
    ...(typeof record.reasoning === 'string' && isReasoningEffort(record.reasoning) ? { reasoning: record.reasoning } : {}),
    ...(typeof record.input === 'string' ? { input: record.input } : {}),
    ...(inputHistory ? { inputHistory } : {}),
  };
}
function readStoredChatSettings(): Partial<ChatSettings> {
  try {
    const stored = window.localStorage.getItem(CHAT_SETTINGS_STORAGE_KEY);
    if (stored) return normalizeChatSettings(JSON.parse(stored));
    const legacyStored = window.localStorage.getItem(LEGACY_CHAT_SETTINGS_STORAGE_KEY);
    if (legacyStored) return normalizeChatSettings(JSON.parse(legacyStored));
    return normalizeChatSettings({
      model: window.localStorage.getItem(LEGACY_CHAT_MODEL_STORAGE_KEY),
      a2uiModel: window.localStorage.getItem(LEGACY_A2UI_MODEL_STORAGE_KEY),
      reasoning: window.localStorage.getItem(LEGACY_REASONING_STORAGE_KEY),
      input: window.localStorage.getItem(LEGACY_INPUT_STORAGE_KEY),
    });
  } catch {
    return {};
  }
}
function writeStoredChatSettings(settings: ChatSettings) {
  try {
    window.localStorage.setItem(CHAT_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {}
}
function clearLegacyChatSettings() {
  try {
    window.localStorage.removeItem(LEGACY_CHAT_SETTINGS_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_CHAT_MODEL_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_A2UI_MODEL_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_REASONING_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_INPUT_STORAGE_KEY);
  } catch {}
}
function usePersistedChatSettings() {
  const [settings, setSettings] = useState<ChatSettings>(DEFAULT_CHAT_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setSettings((current) => ({ ...current, ...readStoredChatSettings() }));
    clearLegacyChatSettings();
    setLoaded(true);
  }, []);
  useEffect(() => {
    if (!loaded) return;
    writeStoredChatSettings(settings);
  }, [loaded, settings]);
  const setInput = useCallback((input: string) => setSettings((current) => (current.input === input ? current : { ...current, input })), []);
  const setSelectedModel = useCallback((model: ChatModel) => setSettings((current) => (current.model === model ? current : { ...current, model })), []);
  const setSelectedA2UIModel = useCallback((a2uiModel: A2UIModel) => setSettings((current) => (current.a2uiModel === a2uiModel ? current : { ...current, a2uiModel })), []);
  const setSelectedReasoning = useCallback((reasoning: ReasoningEffort) => setSettings((current) => (current.reasoning === reasoning ? current : { ...current, reasoning })), []);
  const rememberSubmittedInput = useCallback((text: string) => {
    const normalized = text.trim();
    if (!normalized) return;
    setSettings((current) => ({ ...current, inputHistory: [...current.inputHistory.filter((entry) => entry !== normalized), normalized].slice(-INPUT_HISTORY_LIMIT) }));
  }, []);
  return { ...settings, setInput, setSelectedModel, setSelectedA2UIModel, setSelectedReasoning, rememberSubmittedInput };
}
type ChatReasoningPart = Extract<ChatMessagePart, { type: 'reasoning' }>;
type ConversationFade = { top: boolean; bottom: boolean };

type CanvasToolOutput = {
  ok?: boolean;
  streaming?: boolean;
  surface_id?: string;
  surface_version?: number;
  payload?: Record<string, unknown>;
  error?: string;
};
type ChatCanvasToolPart = Extract<ChatMessagePart, { type: 'tool-canvas' }>;
type ChatWebSearchToolPart = Extract<ChatMessagePart, { type: 'tool-web_search' }>;
type WebSearchSource = { type: 'url'; url: string } | { type: 'api'; name: string };
type WebSearchOutput = { action?: { type?: string; query?: string; queries?: string[]; url?: string }; sources?: WebSearchSource[] };
type ThinkingSummary = { title: string; description: string };
type ThoughtActivity = { active: boolean; seconds: number };

const isCanvasToolPart = (part: ChatMessagePart): part is ChatCanvasToolPart => part.type === 'tool-canvas';
const isCanvasToolOutput = (value: unknown): value is CanvasToolOutput => Boolean(value && typeof value === 'object');
const isWebSearchToolPart = (part: ChatMessagePart): part is ChatWebSearchToolPart => part.type === 'tool-web_search';
const isWebSearchOutput = (value: unknown): value is WebSearchOutput => Boolean(value && typeof value === 'object');

const isTextPart = (part: ChatMessagePart): part is ChatTextPart => part.type === 'text';
const isReasoningPart = (part: ChatMessagePart): part is ChatReasoningPart => part.type === 'reasoning';
const getCanvasPreviewPayload = (output: CanvasToolOutput, fallbackSurfaceId: string) =>
  JSON.stringify(
    {
      canvas_surface: {
        surface_id: output.surface_id || fallbackSurfaceId,
        surface_version: output.surface_version || 1,
        payload: output.payload,
      },
    },
    null,
    2,
  );
const getMessageText = (message: ChatUIMessage) => {
  let text = '';
  for (const part of message.parts) if (isTextPart(part)) text += part.text;
  return text.trim();
};
const isCanvasUserAction = (value: unknown): value is CanvasUserAction => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return ['protocol_version', 'origin_message_id', 'surface_id', 'client_action_id', 'action_name', 'source_component_id'].every((key) => typeof record[key] === 'string') && typeof record.surface_version === 'number';
};
const formatActionContext = (context: CanvasUserAction['context']) => {
  if (!context || Object.keys(context).length === 0) return '';
  const simpleEntries = Object.entries(context).filter((entry): entry is [string, string | number | boolean] => ['string', 'number', 'boolean'].includes(typeof entry[1]));
  return simpleEntries.length > 0 ? simpleEntries.map(([key, value]) => `${key}: ${String(value)}`).join(', ') : JSON.stringify(context);
};
const getCanvasActionDisplayText = (action: CanvasUserAction) => {
  const context = formatActionContext(action.context);
  return context ? `Canvas action: ${action.action_name} (${context})` : `Canvas action: ${action.action_name}`;
};
const getCanvasActionPrompt = (action: CanvasUserAction) =>
  [
    'Canvas user action from the rendered A2UI surface. Continue the conversation from this interaction.',
    `action_name: ${action.action_name}`,
    `source_component_id: ${action.source_component_id}`,
    `surface_id: ${action.surface_id}`,
    `surface_version: ${action.surface_version}`,
    `origin_message_id: ${action.origin_message_id}`,
    `client_action_id: ${action.client_action_id}`,
    `context: ${action.context && Object.keys(action.context).length > 0 ? JSON.stringify(action.context) : 'null'}`,
  ].join('\n');
const getNodeText = (value: ReactNode): string => {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(getNodeText).join('');
  if (isValidElement<{ children?: ReactNode }>(value)) return getNodeText(value.props.children);
  return '';
};
const hasVisibleAssistantContent = (message: ChatUIMessage | undefined) => message?.role === 'assistant' && message.parts.some((part) => (isTextPart(part) || isReasoningPart(part)) && part.text.trim());
const hasAssistantToolActivity = (message: ChatUIMessage | undefined) =>
  message?.role === 'assistant' && message.parts.some((part) => (isCanvasToolPart(part) || isWebSearchToolPart(part)) && (part.state === 'input-streaming' || part.state === 'input-available' || part.state === 'output-available'));
const getElapsedSeconds = (startedAt: number | undefined, now: number) => Math.max(Math.floor((now - (startedAt || now)) / 1000), 0);
const getThoughtActivityLabel = ({ active, seconds }: ThoughtActivity) => (active ? (seconds === 0 ? 'Thinking' : `Thinking for ${seconds}s`) : `Thought for ${seconds}s`);
const getConversationScrollState = (node: HTMLElement): { fade: ConversationFade; isNearBottom: boolean } => {
  const canScroll = node.scrollHeight - node.clientHeight > 1;
  const distanceFromBottom = Math.max(node.scrollHeight - node.clientHeight - node.scrollTop, 0);
  return { fade: canScroll ? { top: node.scrollTop > 1, bottom: distanceFromBottom > BOTTOM_STICK_THRESHOLD } : { top: false, bottom: false }, isNearBottom: !canScroll || distanceFromBottom <= BOTTOM_STICK_THRESHOLD };
};

type MarkdownCodeProps = ComponentPropsWithoutRef<'code'> & { 'data-block'?: string; node?: unknown };

function MarkdownCode({ children, className, node: _node, ...props }: MarkdownCodeProps) {
  const isBlock = 'data-block' in props;
  const language = className?.match(codeLanguagePattern)?.[1]?.toLowerCase();
  if (!isBlock) return <code className="chat-inline-code">{children}</code>;
  const code = getNodeText(children).replace(/\n$/, '');
  return (
    <div className="chat-code-block">
      {language ? <div className="chat-code-block-header">{language}</div> : null}
      <pre className="chat-code-block-pre">
        <code className="chat-code-block-code">{code}</code>
      </pre>
    </div>
  );
}

const markdownComponents: Components = { code: MarkdownCode };
const markdownPlugins = { cjk } as const;
const markdownLinkSafety = { enabled: false } as const;
const markdownAnimatedConfig = { animation: 'fadeIn', duration: 800, sep: 'char', stagger: 0 } as const;
const reasoningSummaryAnimatedConfig = { animation: 'fadeIn', duration: 300, sep: 'word', stagger: 0 } as const;
const thinkingSummaryTitlePattern = /(?:^|\n)\s*\*\*([^*\n]+)\*\*\s*(?=\n|$)/g;

function ChatMarkdown({
  text,
  isStreaming,
  className = 'chat-markdown',
  animated = markdownAnimatedConfig,
}: {
  text: string;
  isStreaming: boolean;
  className?: string;
  animated?: typeof markdownAnimatedConfig | typeof reasoningSummaryAnimatedConfig;
}) {
  return (
    <Streamdown animated={animated} className={className} components={markdownComponents} controls={false} lineNumbers={false} linkSafety={markdownLinkSafety} plugins={markdownPlugins} isAnimating={isStreaming}>
      {text}
    </Streamdown>
  );
}

const getURLDomain = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};
const getWebSearchQuery = (output: WebSearchOutput | undefined) => output?.action?.query || output?.action?.queries?.[0];
const isWebPageReadAction = (output: WebSearchOutput | undefined) => output?.action?.type === 'openPage' || output?.action?.type === 'open_page';
const getWebSearchLabel = (output: WebSearchOutput | undefined, fallback: string) => {
  const query = getWebSearchQuery(output);
  if (query) return query;
  const action = output?.action;
  if (isWebPageReadAction(output)) return action?.url ? `Opened ${getURLDomain(action.url)}` : 'Opened a source';
  if (action?.type === 'findInPage' || action?.type === 'find_in_page') return 'Searched within a source';
  return fallback;
};
const getSourceKey = (source: WebSearchSource) => (source.type === 'url' ? getURLDomain(source.url) : source.name);
const getSourceFavicon = (source: WebSearchSource) => {
  if (source.type !== 'url') return undefined;
  try {
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(new URL(source.url).hostname)}&sz=32`;
  } catch {
    return undefined;
  }
};
const getUniqueSources = (sources: WebSearchSource[] = []) => {
  const seen = new Set<string>();
  return sources.filter((source) => {
    const key = getSourceKey(source);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
const getWebSearchSources = (output: WebSearchOutput | undefined) => {
  const sources = getUniqueSources(output?.sources);
  const url = output?.action?.url;
  if (!url || sources.some((source) => source.type === 'url' && source.url === url)) return sources;
  return [{ type: 'url' as const, url }, ...sources];
};
const parseThinkingSummaries = (text: string): ThinkingSummary[] => {
  const summaries: ThinkingSummary[] = [];
  const titleMatches = [...text.matchAll(thinkingSummaryTitlePattern)];
  for (const [index, match] of titleMatches.entries()) {
    const title = match[1]?.trim();
    const descriptionStart = (match.index ?? 0) + match[0].length;
    const descriptionEnd = titleMatches[index + 1]?.index ?? text.length;
    const description = text.slice(descriptionStart, descriptionEnd).trim();
    if (title && description) summaries.push({ title, description });
  }
  return summaries;
};

function CoTRail({ icon, label, children }: { icon: ReactNode; label: string; children?: ReactNode }) {
  const hasDetails = Boolean(children);
  return (
    <div className={hasDetails ? 'grid grid-cols-[1.25rem_minmax(0,1fr)] grid-rows-[1.25rem_15px_auto] gap-x-3 text-[#6f655b]' : 'grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-3 text-[#6f655b]'}>
      <div className="col-start-1 row-start-1 grid h-5 w-5 place-items-center">{icon}</div>
      {/* The three-row grid keeps the 15px label/result gap as the single spacing source, and lets the rail line share the exact top/bottom with wrapped result badges. */}
      {/* 2px is the closest visual match for the search icon stroke; 3px makes the rail read heavier than the icon. */}
      {hasDetails ? <div className="col-start-1 row-start-3 mx-auto h-full w-[2px] rounded-full bg-[#2a2a29]/[0.07]" /> : null}
      <div className="col-start-2 row-start-1 min-w-0 truncate text-[13px] leading-5">{label}</div>
      {hasDetails ? <div className="col-start-2 row-start-3 min-w-0">{children}</div> : null}
    </div>
  );
}

const ThinkingSummaryActivity = memo(function ThinkingSummaryActivity({ title, description, isStreaming }: ThinkingSummary & { isStreaming: boolean }) {
  return (
    <CoTRail icon={<Milestone className="h-3.5 w-3.5 shrink-0 text-[#9a8d80]" strokeWidth={2.25} />} label={title}>
      <ChatMarkdown text={description} isStreaming={isStreaming} className="chat-markdown chat-cot-markdown chat-cot-summary-markdown max-w-[48rem]" animated={reasoningSummaryAnimatedConfig} />
    </CoTRail>
  );
});

function CanvasToolActivity({ active = true, label = 'Creating A2UI canvas' }: { active?: boolean; label?: string } = {}) {
  return <CoTRail icon={<MousePointer2 className={`h-3.5 w-3.5 shrink-0 ${active ? 'animate-pulse text-[#EE5C2A]' : 'text-[#9a8d80]'}`} strokeWidth={2.25} />} label={label} />;
}

function ThoughtTimerActivity({ activity }: { activity: ThoughtActivity }) {
  return <CoTRail icon={<Power className={`h-3.5 w-3.5 shrink-0 ${activity.active ? 'animate-pulse text-[#EE5C2A]' : 'text-[#9a8d80]'}`} strokeWidth={2.25} />} label={getThoughtActivityLabel(activity)} />;
}

function WebSearchSourceBadges({ sources }: { sources: WebSearchSource[] }) {
  if (!sources.length) return null;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[12px] leading-5 text-[#6f655b]">
      {sources.map((source) => {
        const label = getSourceKey(source);
        const favicon = getSourceFavicon(source);
        const content = (
          <>
            {favicon ? <img src={favicon} alt="" className="h-3.5 w-3.5 shrink-0 rounded-[3px]" loading="lazy" onError={(event) => (event.currentTarget.style.display = 'none')} /> : null}
            <span className="max-w-[11rem] truncate">{label}</span>
          </>
        );
        const className =
          'inline-flex min-w-0 max-w-[12.5rem] items-center gap-1.5 rounded-full border border-[#2a2a29]/[0.07] bg-white/45 px-2 py-0.5 no-underline shadow-[0_1px_1px_rgba(42,34,24,0.03)] transition hover:border-[#2a2a29]/[0.12] hover:bg-white/70 hover:text-[#4f4943]';
        return source.type === 'url' ? (
          <a key={source.url} href={source.url} target="_blank" rel="noreferrer" className={className}>
            {content}
          </a>
        ) : (
          <span key={source.name} className={className}>
            {content}
          </span>
        );
      })}
    </div>
  );
}

function WebSearchActivity({ part }: { part: ChatWebSearchToolPart }) {
  const output = part.state === 'output-available' && isWebSearchOutput(part.output) ? part.output : undefined;
  const sources = getWebSearchSources(output);
  const active = part.state === 'input-streaming' || part.state === 'input-available' || ('preliminary' in part && Boolean(part.preliminary));
  const query = active ? getWebSearchLabel(output, 'Searching the web...') : getWebSearchLabel(output, part.state === 'output-error' ? 'Search failed' : 'the web');
  const label = active ? query : query.startsWith('Opened ') ? query : `Searched ${query}`;
  const error = part.state === 'output-error' ? part.errorText : undefined;
  const Icon = isWebPageReadAction(output) ? Eye : Search;
  const details =
    sources.length || error ? (
      <div className="grid gap-1.5">
        <WebSearchSourceBadges sources={sources} />
        {error ? <div className="text-[12px] leading-5 text-[#b4233a]">{error}</div> : null}
      </div>
    ) : undefined;
  return (
    <CoTRail icon={<Icon className={`h-3.5 w-3.5 shrink-0 ${active ? 'animate-pulse text-[#EE5C2A]' : 'text-[#9a8d80]'}`} strokeWidth={2.25} />} label={label}>
      {details}
    </CoTRail>
  );
}

function WebSearchCluster({ parts }: { parts: ChatWebSearchToolPart[] }) {
  return (
    // Match result-bottom -> next-label spacing to label-bottom -> result-top spacing for concurrent web search calls.
    <div className="mb-[3px] grid w-full gap-[15px]">
      {parts.map((part) => (
        <WebSearchActivity key={part.toolCallId} part={part} />
      ))}
    </div>
  );
}

function CanvasPreviewOpenForm({ payload }: { payload: string }) {
  return (
    <form action="/api/a2ui-preview/import" method="post" target="_blank">
      <input type="hidden" name="payload" value={payload} />
      <button
        type="submit"
        className="grid h-9 w-9 place-items-center rounded-xl border border-[#e7ddd2] bg-white text-[#8a7d71] shadow-[0_1px_2px_rgba(42,42,41,0.05)] transition hover:border-[#d8cabc] hover:text-[#2a2a29]"
        aria-label="Open canvas in A2UI preview"
      >
        <ExternalLink className="h-4 w-4" aria-hidden="true" />
      </button>
    </form>
  );
}

function SendStopButtonContent({ busy, disabled = false }: { busy: boolean; disabled?: boolean }) {
  return (
    <span className={disabled ? 'relative grid h-10 w-10 place-items-center text-[#6f655b]' : 'relative grid h-10 w-10 place-items-center text-white'} aria-hidden="true">
      <motion.span
        className={disabled ? 'absolute bg-[#eee6dc]' : 'absolute bg-[#2a2a29]'}
        initial={false}
        animate={{ width: busy ? 14 : 40, height: busy ? 14 : 40, borderRadius: busy ? 4 : 999 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      />
      <motion.svg className="absolute h-5 w-5" viewBox="0 0 20 20" fill="none" initial={false} animate={{ y: busy ? -2 : 0, scale: busy ? 0.72 : 1, opacity: busy ? 0 : 1 }} transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}>
        <path d="M10 15V5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M5.5 9.5 10 5l4.5 4.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </motion.svg>
    </span>
  );
}

function ChatMessage({ message, isStreaming, thoughtActivity }: { message: ChatUIMessage; isStreaming: boolean; thoughtActivity?: ThoughtActivity }) {
  if (message.role === 'user') {
    const text = message.metadata?.canvasUserAction ? getCanvasActionDisplayText(message.metadata.canvasUserAction) : getMessageText(message);
    if (!text) return null;
    return (
      <div className="flex w-full justify-end py-2 pl-8 md:w-[min(768px,100%)] md:py-3 md:pl-10">
        <div className="max-w-full whitespace-pre-wrap break-words rounded-2xl bg-black/[0.04] px-3.5 py-2 font-sans text-[14px] leading-5 text-[#2a2a29] md:px-4 md:py-2.5 md:text-[16px] md:leading-6">{text}</div>
      </div>
    );
  }

  const parts = message.parts.flatMap((part, index) => {
    if (isWebSearchToolPart(part)) {
      if (isWebSearchToolPart(message.parts[index - 1])) return [];
      const searchParts: ChatWebSearchToolPart[] = [];
      for (const searchPart of message.parts.slice(index)) {
        if (!isWebSearchToolPart(searchPart)) break;
        searchParts.push(searchPart);
      }
      return [<WebSearchCluster key={`${message.id}-${index}`} parts={searchParts} />];
    }
    if (isReasoningPart(part) && part.text.trim()) {
      const summaries = parseThinkingSummaries(part.text);
      if (summaries.length)
        return [
          // Parent message parts use gap-3 (12px); the extra 3px keeps summary-detail -> next-CoT-label spacing equal to the rail's 15px label -> detail gap.
          <div key={`${message.id}-${index}`} className="mb-[3px] grid w-full gap-[15px]">
            {summaries.map((summary, summaryIndex) => (
              // Keep streaming descriptions out of the key; remounting restarts Streamdown's word fade-in and looks like flicker.
              <ThinkingSummaryActivity key={`${summary.title}-${summaryIndex}`} title={summary.title} description={summary.description} isStreaming={isStreaming && part.state !== 'done'} />
            ))}
          </div>,
        ];
      return [
        <div key={`${message.id}-${index}`} className="chat-markdown-bubble">
          <ChatMarkdown text={part.text} isStreaming={isStreaming && part.state !== 'done'} className="chat-markdown chat-cot-markdown" />
        </div>,
      ];
    }
    if (isTextPart(part) && part.text.trim()) {
      return [
        <div key={`${message.id}-${index}`} className="chat-markdown-bubble">
          <ChatMarkdown text={part.text} isStreaming={isStreaming && part.state !== 'done'} />
        </div>,
      ];
    }
    if (isCanvasToolPart(part) && part.state === 'output-available' && isCanvasToolOutput(part.output)) {
      if (!part.output.ok)
        return [
          <div key={`${message.id}-${index}`} className="grid gap-3">
            <CanvasToolActivity active={false} label="Canvas creation failed" />
            <div className="rounded-2xl bg-[#ff5a70]/10 px-4 py-2 text-[13px] text-[#b4233a]">Canvas failed: {part.output.error || 'unknown error'}</div>
          </div>,
        ];
      const previewPayload = getCanvasPreviewPayload(part.output, part.toolCallId);
      const stillStreaming = Boolean(part.output.streaming || part.preliminary);
      return [
        <div key={`${message.id}-${index}`} className="grid gap-3">
          <CanvasToolActivity active={stillStreaming} label={stillStreaming ? 'Creating A2UI canvas' : 'Created A2UI canvas'} />
          <div className="group/canvas relative">
            <div className="sticky top-4 z-20 h-0 w-0 overflow-visible">
              <div className="pointer-events-none absolute -left-11 top-2 opacity-0 transition group-hover/canvas:pointer-events-auto group-hover/canvas:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100">
                <CanvasPreviewOpenForm payload={previewPayload} />
              </div>
            </div>
            {part.preliminary || part.output.streaming ? (
              <A2UIMessageRenderer surfaceId={part.output.surface_id || part.toolCallId} originMessageId={message.id} surfaceVersion={part.output.surface_version || 1} canvasPayload={part.output.payload} />
            ) : (
              <InteractiveA2UIMessageRenderer surfaceId={part.output.surface_id || part.toolCallId} originMessageId={message.id} surfaceVersion={part.output.surface_version || 1} canvasPayload={part.output.payload} />
            )}
          </div>
        </div>,
      ];
    }
    if (isCanvasToolPart(part) && (part.state === 'input-streaming' || part.state === 'input-available')) return [<CanvasToolActivity key={`${message.id}-${index}`} />];
    if (isCanvasToolPart(part) && part.state === 'output-error')
      return [
        <div key={`${message.id}-${index}`} className="grid gap-3">
          <CanvasToolActivity active={false} label="Canvas creation failed" />
          <div className="rounded-2xl bg-[#ff5a70]/10 px-4 py-2 text-[13px] text-[#b4233a]">Canvas failed: {part.errorText || 'unknown error'}</div>
        </div>,
      ];
    return [];
  });
  const displayParts = thoughtActivity ? [<ThoughtTimerActivity key={`${message.id}-thought-timer`} activity={thoughtActivity} />, ...parts] : parts;
  if (!displayParts.length) return null;

  return (
    <div className="flex w-full justify-start py-2 md:w-[min(768px,100%)] md:py-3">
      <div className="grid w-full gap-3">{displayParts}</div>
    </div>
  );
}

function MorphSelect<T extends string>({ label, icon: LabelIcon, value, options, onChange }: { label: string; icon: LucideIcon; value: T; options: readonly MorphSelectOption<T>[]; onChange: (value: T) => void }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [openDirection, setOpenDirection] = useState<MorphSelectOpenDirection>('down');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const collapsedMeasureRef = useRef<HTMLButtonElement | null>(null);
  const expandedMeasureRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [measuredWidth, setMeasuredWidth] = useState<{ collapsed: number; expanded: number } | null>(null);
  const selectedOption = options.find((option) => option.value === value) ?? options[0];
  const selectedIndex = Math.max(
    options.findIndex((option) => option.value === value),
    0,
  );
  const optionPanelHeight = options.length * morphSelectOptionHeight + morphSelectPanelChromeHeight;
  const expandedHeight = morphSelectTriggerHeight + optionPanelHeight;
  const selectedShortLabel = selectedOption.shortLabel ?? selectedOption.label;
  const targetWidth = measuredWidth ? (open ? measuredWidth.expanded : measuredWidth.collapsed) : undefined;
  const syncOpenDirection = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const neededSpace = optionPanelHeight + morphSelectViewportMargin;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenDirection(spaceBelow < neededSpace && spaceAbove > spaceBelow ? 'up' : 'down');
  }, [optionPanelHeight]);

  useLayoutEffect(() => {
    const syncMeasuredWidth = () => {
      const collapsed = collapsedMeasureRef.current?.getBoundingClientRect().width ?? 0;
      const expanded = expandedMeasureRef.current?.getBoundingClientRect().width ?? collapsed;
      if (!collapsed) return;
      setMeasuredWidth((current) => {
        const next = { collapsed, expanded: Math.max(collapsed, expanded) };
        return current && Math.abs(current.collapsed - next.collapsed) < 0.5 && Math.abs(current.expanded - next.expanded) < 0.5 ? current : next;
      });
    };
    syncMeasuredWidth();
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(syncMeasuredWidth);
    if (collapsedMeasureRef.current) resizeObserver?.observe(collapsedMeasureRef.current);
    if (expandedMeasureRef.current) resizeObserver?.observe(expandedMeasureRef.current);
    window.addEventListener('resize', syncMeasuredWidth);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncMeasuredWidth);
    };
  }, [label, options, selectedOption.label, selectedShortLabel]);

  const focusOption = useCallback(
    (index: number) => {
      const nextIndex = (index + options.length) % options.length;
      setActiveIndex(nextIndex);
      // Debug history: several earlier morph-select attempts looked correct when the selected item was near the top, but felt broken when the selected item was lower in the list. The culprit was not Motion layout animation; it was the browser helpfully scrolling the focused option into view inside the clipped dropdown. That scroll shifted the whole option stack downward, so the items no longer appeared pinned under the trigger. `preventScroll` keeps keyboard focus for accessibility while preserving the visual invariant: the list is always top-aligned and only the outer shell reveals more of it.
      optionRefs.current[nextIndex]?.focus({ preventScroll: true });
    },
    [options.length],
  );

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    syncOpenDirection();
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', syncOpenDirection);
    window.addEventListener('scroll', syncOpenDirection, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', syncOpenDirection);
      window.removeEventListener('scroll', syncOpenDirection, true);
    };
  }, [open, syncOpenDirection]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(selectedIndex);
    const frameId = requestAnimationFrame(() => focusOption(selectedIndex));
    return () => cancelAnimationFrame(frameId);
  }, [focusOption, open, selectedIndex]);

  const closeModelSelector = () => {
    setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };
  const openModelSelector = () => {
    syncOpenDirection();
    setOpen(true);
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openModelSelector();
      requestAnimationFrame(() => focusOption(event.key === 'ArrowDown' ? selectedIndex + 1 : selectedIndex - 1));
    }
  };

  const handleOptionKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      focusOption(event.key === 'ArrowDown' ? index + 1 : index - 1);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeModelSelector();
    }
  };

  return (
    <motion.div ref={containerRef} className="relative h-9 w-max max-w-full shrink-0" initial={false} animate={targetWidth ? { width: targetWidth } : {}} transition={{ width: morphSelectWidthTransition }}>
      <button ref={collapsedMeasureRef} type="button" tabIndex={-1} aria-hidden="true" className="invisible flex h-9 items-center gap-2 whitespace-nowrap px-3 text-[12px] font-medium">
        <LabelIcon data-chat-setting-icon className="hidden h-4 w-4 shrink-0" strokeWidth={2.2} />
        <span data-chat-setting-label>{label}</span>
        <span data-chat-setting-value>{selectedOption.label}</span>
        <span data-chat-setting-value-short className="hidden">
          {selectedShortLabel}
        </span>
        <ChevronDown className="h-4 w-4" strokeWidth={2.2} />
      </button>
      <div ref={expandedMeasureRef} aria-hidden="true" className="invisible absolute left-0 top-0 w-max overflow-hidden p-1 text-[13px] font-medium">
        {options.map((option) => (
          <div key={option.value} className="mx-1 flex h-8 items-center gap-2 rounded-[12px] px-3">
            <Check className="h-4 w-4 shrink-0" strokeWidth={2.4} />
            <span className="whitespace-nowrap">{option.label}</span>
          </div>
        ))}
      </div>
      <motion.div
        initial={false}
        animate={{ height: open ? expandedHeight : morphSelectTriggerHeight, boxShadow: open ? '0 14px 30px rgba(42,34,24,0.10)' : '0 0 0 rgba(42,34,24,0)' }}
        transition={{ height: morphSelectTransition, boxShadow: morphSelectContentTransition }}
        style={{ borderRadius: 18 }}
        className={
          openDirection === 'up'
            ? 'absolute bottom-0 left-0 z-30 min-w-full overflow-hidden bg-white/95 text-[13px] font-medium text-[#4f4943] backdrop-blur'
            : 'absolute left-0 top-0 z-30 min-w-full overflow-hidden bg-white/95 text-[13px] font-medium text-[#4f4943] backdrop-blur'
        }
      >
        {/* This component intentionally morphs a single visible shell instead of mounting a separate popover. Earlier versions animated `height: auto` around normal-flow options, used `clip-path` while the options still contributed layout, or changed border radius during the transition; those versions caused the composer to stretch, created a blob-like intermediate shape, or made the option rows feel like they were being dragged down. The final structure keeps the trigger and options pinned inside one shell, choosing a top or bottom origin from available viewport space. Only the shell size and shadow animate, so trigger row and option row coordinates stay stable. */}
        <button
          type="button"
          ref={triggerRef}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`${label}: ${selectedOption.label}`}
          title={`${label}: ${selectedOption.label}`}
          onClick={() => (open ? setOpen(false) : openModelSelector())}
          onKeyDown={handleTriggerKeyDown}
          className={
            openDirection === 'up'
              ? 'absolute bottom-0 left-0 flex h-9 w-full items-center gap-2 whitespace-nowrap px-3 text-left text-[12px] text-[#6f655b] disabled:cursor-not-allowed disabled:opacity-60'
              : 'absolute left-0 top-0 flex h-9 w-full items-center gap-2 whitespace-nowrap px-3 text-left text-[12px] text-[#6f655b] disabled:cursor-not-allowed disabled:opacity-60'
          }
        >
          <LabelIcon data-chat-setting-icon className="hidden h-4 w-4 shrink-0" strokeWidth={2.2} />
          <span data-chat-setting-label>{label}</span>
          <span data-chat-setting-value className="text-[#2a2a29]">
            {selectedOption.label}
          </span>
          <span data-chat-setting-value-short className="hidden text-[#2a2a29]">
            {selectedShortLabel}
          </span>
          <ChevronDown className={open ? 'ml-auto h-4 w-4 rotate-180 text-[#2a2a29] transition' : 'ml-auto h-4 w-4 text-[#2a2a29] transition'} strokeWidth={2.2} />
        </button>
        <div
          role="listbox"
          className={
            open
              ? openDirection === 'up'
                ? 'pointer-events-auto absolute bottom-9 left-0 w-full border-b border-[#2a2a29]/[0.06] p-1'
                : 'pointer-events-auto absolute left-0 top-9 w-full border-t border-[#2a2a29]/[0.06] p-1'
              : openDirection === 'up'
                ? 'pointer-events-none absolute bottom-9 left-0 w-full border-b border-[#2a2a29]/[0.06] p-1'
                : 'pointer-events-none absolute left-0 top-9 w-full border-t border-[#2a2a29]/[0.06] p-1'
          }
        >
          {options.map((option, index) => {
            const selected = option.value === value;
            const active = index === activeIndex;
            return (
              <button
                key={option.value}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                type="button"
                role="option"
                tabIndex={open ? 0 : -1}
                data-active={active ? 'true' : undefined}
                data-selected={selected ? 'true' : undefined}
                aria-selected={selected}
                onClick={() => {
                  onChange(option.value);
                  closeModelSelector();
                }}
                onKeyDown={(event) => handleOptionKeyDown(event, index)}
                onFocus={() => setActiveIndex(index)}
                onMouseEnter={() => setActiveIndex(index)}
                className="flex h-8 w-full items-center gap-2 rounded-[12px] px-3 text-left transition data-[active=true]:bg-[#f8f5ef] data-[active=true]:text-[#2a2a29] data-[selected=true]:text-[#2a2a29] data-[selected=true]:not-data-[active=true]:bg-[#f3eee7]"
              >
                <Check className={selected ? 'h-4 w-4 shrink-0 text-[#ff5a70]' : 'h-4 w-4 shrink-0 opacity-0'} strokeWidth={2.4} />
                <span className="whitespace-nowrap">{option.label}</span>
              </button>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}

function ChatComposer({
  input,
  setInput,
  inputHistory,
  textareaHeight,
  setTextareaHeight,
  isBusy,
  onSubmit,
  onStop,
  onFocus,
  onBlur,
  footer,
}: {
  input: string;
  setInput: (value: string) => void;
  inputHistory: string[];
  textareaHeight: number;
  setTextareaHeight: (height: number) => void;
  isBusy: boolean;
  onSubmit: (value: string) => void;
  onStop: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  footer?: ReactNode;
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const inputHistoryIndexRef = useRef<number | null>(null);
  const inputHistoryDraftRef = useRef('');
  const inputHistoryNavigationLockedRef = useRef(false);
  const hasInput = input.trim().length > 0;

  const exitInputHistoryNavigation = useCallback(() => {
    inputHistoryIndexRef.current = null;
    inputHistoryDraftRef.current = '';
    inputHistoryNavigationLockedRef.current = false;
  }, []);

  const focusTextareaAtEnd = useCallback(() => {
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const caret = textarea.value.length;
      textarea.focus();
      textarea.setSelectionRange(caret, caret);
    });
  }, []);

  const navigateInputHistory = useCallback(
    (direction: 'older' | 'newer') => {
      if (inputHistory.length === 0) return;
      const currentIndex = inputHistoryIndexRef.current;
      if (direction === 'older') {
        if (currentIndex == null) {
          inputHistoryDraftRef.current = input;
          inputHistoryIndexRef.current = inputHistory.length - 1;
        } else if (currentIndex > 0) inputHistoryIndexRef.current = currentIndex - 1;
      } else {
        if (currentIndex == null) return;
        if (currentIndex === inputHistory.length - 1) {
          setInput(inputHistoryDraftRef.current);
          exitInputHistoryNavigation();
          focusTextareaAtEnd();
          return;
        }
        inputHistoryIndexRef.current = currentIndex + 1;
      }
      setInput(inputHistory[inputHistoryIndexRef.current ?? -1] ?? inputHistoryDraftRef.current);
      inputHistoryNavigationLockedRef.current = true;
      focusTextareaAtEnd();
    },
    [exitInputHistoryNavigation, focusTextareaAtEnd, input, inputHistory, setInput],
  );

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    if (!input) {
      setTextareaHeight(COMPOSER_MIN_HEIGHT);
      return;
    }
    const previousHeight = textarea.style.height;
    textarea.style.height = 'auto';
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, COMPOSER_MIN_HEIGHT), COMPOSER_MAX_HEIGHT);
    textarea.style.height = previousHeight;
    setTextareaHeight(nextHeight);
  }, [input, setTextareaHeight]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = event.currentTarget;
    const hasSelection = textarea.selectionStart !== textarea.selectionEnd;
    const caretAtEnd = textarea.selectionStart === textarea.value.length && textarea.selectionEnd === textarea.value.length;
    const historyModifier = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
    if (event.key === 'ArrowUp' && !historyModifier && !hasSelection && (inputHistoryNavigationLockedRef.current ? inputHistoryIndexRef.current != null && caretAtEnd : textarea.value.length === 0)) {
      event.preventDefault();
      navigateInputHistory('older');
      return;
    }
    if (event.key === 'ArrowDown' && !historyModifier && !hasSelection && inputHistoryNavigationLockedRef.current && inputHistoryIndexRef.current != null && caretAtEnd) {
      event.preventDefault();
      navigateInputHistory('newer');
      return;
    }
    if (inputHistoryNavigationLockedRef.current) exitInputHistoryNavigation();
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      onSubmit(input);
    }
  };

  return (
    <form
      className="flex w-full flex-col gap-4 rounded-[20px] bg-[#f8f5ef] px-4 py-4 md:gap-5 md:rounded-[24px] md:px-6 md:py-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (inputHistoryNavigationLockedRef.current) exitInputHistoryNavigation();
        onSubmit(input);
      }}
    >
      <div className="overflow-hidden">
        <textarea
          ref={textareaRef}
          data-testid="a2ui-chat-input"
          value={input}
          onChange={(event) => {
            if (inputHistoryNavigationLockedRef.current) exitInputHistoryNavigation();
            setInput(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          onPointerDown={() => {
            if (inputHistoryNavigationLockedRef.current) exitInputHistoryNavigation();
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder="Ask about A2UI payloads, component states, or product ideas..."
          className="m-0 block min-h-10 w-full resize-none border-0 bg-transparent p-0 text-[15px] leading-6 text-[#2a2a29] outline-none placeholder:text-[#8f857b] md:text-[16px]"
          style={{ height: `${textareaHeight}px`, maxHeight: `${COMPOSER_MAX_HEIGHT}px`, overflowY: textareaHeight > COMPOSER_MIN_HEIGHT ? 'auto' : 'hidden' }}
          rows={1}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">{footer}</div>
        {isBusy ? (
          <button type="button" aria-label="Stop response" onClick={onStop} className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#eee6dc] transition duration-200 hover:opacity-80 active:scale-95">
            <SendStopButtonContent busy />
          </button>
        ) : (
          <button type="submit" disabled={!hasInput} className="grid h-10 w-10 shrink-0 place-items-center rounded-full transition duration-200 hover:opacity-80 active:scale-95 disabled:cursor-not-allowed disabled:opacity-100">
            <SendStopButtonContent busy={false} disabled={!hasInput} />
          </button>
        )}
      </div>
    </form>
  );
}

export default function ChatPage() {
  const {
    input,
    inputHistory,
    model: selectedModel,
    a2uiModel: selectedA2UIModel,
    reasoning: selectedReasoning,
    setInput,
    setSelectedModel,
    setSelectedA2UIModel,
    setSelectedReasoning,
    rememberSubmittedInput,
  } = usePersistedChatSettings();
  const [textareaHeight, setTextareaHeight] = useState(COMPOSER_MIN_HEIGHT);
  const [conversationFade, setConversationFade] = useState<ConversationFade>({ top: false, bottom: false });
  const [now, setNow] = useState(() => Date.now());
  const [activeThought, setActiveThought] = useState<{ startedAt: number; assistantMessageId?: string } | null>(null);
  const [thoughtDurations, setThoughtDurations] = useState<Record<string, number>>({});
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const conversationRef = useRef<HTMLElement | null>(null);
  const shouldStickToBottomRef = useRef(true);
  const { messages, sendMessage, stop, status, error } = useChat<ChatUIMessage>({ transport: chatTransport, messageMetadataSchema: chatMessageMetadataSchema });
  const visibleMessages = useMemo(() => messages.filter((message) => message.role === 'user' || message.role === 'assistant'), [messages]);
  const hasUserMessages = visibleMessages.some((message) => message.role === 'user');
  const lastVisibleMessage = visibleMessages.at(-1);
  const isBusy = status === 'submitted' || status === 'streaming';
  const shouldShowPending = status === 'submitted' || (status === 'streaming' && !hasVisibleAssistantContent(lastVisibleMessage) && !hasAssistantToolActivity(lastVisibleMessage));
  const pendingThoughtActivity = shouldShowPending && activeThought ? { active: true, seconds: getElapsedSeconds(activeThought.startedAt, now) } : undefined;

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey || event.altKey || isEditableTarget(event.target)) return;
      if (event.key === '/') {
        event.preventDefault();
        focusChatInput();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!isBusy) return;
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [isBusy]);

  useEffect(() => {
    if (!activeThought) return;
    const latestAssistantMessage = [...visibleMessages].toReversed().find((message) => message.role === 'assistant');
    if (latestAssistantMessage && activeThought.assistantMessageId !== latestAssistantMessage.id) {
      setActiveThought((current) => (current ? { ...current, assistantMessageId: latestAssistantMessage.id } : current));
      return;
    }
    if (!isBusy && activeThought.assistantMessageId) {
      const seconds = Math.max(getElapsedSeconds(activeThought.startedAt, Date.now()), 1);
      setThoughtDurations((current) => ({ ...current, [activeThought.assistantMessageId as string]: seconds }));
      setActiveThought(null);
    }
  }, [activeThought, isBusy, visibleMessages]);

  useEffect(() => {
    const node = conversationRef.current;
    if (!node || !shouldStickToBottomRef.current) return;
    bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    const frameId = requestAnimationFrame(() => {
      const activeNode = conversationRef.current;
      if (!activeNode) return;
      const nextState = getConversationScrollState(activeNode);
      shouldStickToBottomRef.current = nextState.isNearBottom;
      setConversationFade((current) => (current.top === nextState.fade.top && current.bottom === nextState.fade.bottom ? current : nextState.fade));
    });
    return () => cancelAnimationFrame(frameId);
  }, [messages, status]);

  useEffect(() => {
    const node = conversationRef.current;
    if (!node) return;
    const syncConversationFade = () => {
      const nextState = getConversationScrollState(node);
      shouldStickToBottomRef.current = nextState.isNearBottom;
      setConversationFade((current) => (current.top === nextState.fade.top && current.bottom === nextState.fade.bottom ? current : nextState.fade));
    };
    let frameId = 0;
    const scheduleConversationFadeSync = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(syncConversationFade);
    };
    scheduleConversationFadeSync();
    node.addEventListener('scroll', scheduleConversationFadeSync, { passive: true });
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleConversationFadeSync);
    resizeObserver?.observe(node);
    window.addEventListener('resize', scheduleConversationFadeSync);
    return () => {
      cancelAnimationFrame(frameId);
      node.removeEventListener('scroll', scheduleConversationFadeSync);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleConversationFadeSync);
    };
  }, [hasUserMessages, visibleMessages.length, status]);

  const submitText = (value: string) => {
    const text = value.trim();
    if (!text || isBusy) return;
    shouldStickToBottomRef.current = true;
    setNow(Date.now());
    setActiveThought({ startedAt: Date.now() });
    rememberSubmittedInput(text);
    void sendMessage({ text }, { body: { model: selectedModel, reasoningEffort: selectedReasoning, a2uiModel: selectedA2UIModel } });
    setInput('');
    setTextareaHeight(COMPOSER_MIN_HEIGHT);
  };

  const submitCanvasUserAction = useCallback(
    (action: CanvasUserAction) => {
      if (isBusy) return;
      shouldStickToBottomRef.current = true;
      setNow(Date.now());
      setActiveThought({ startedAt: Date.now() });
      void sendMessage({ text: getCanvasActionPrompt(action), metadata: { canvasUserAction: action } }, { body: { model: selectedModel, reasoningEffort: selectedReasoning, a2uiModel: selectedA2UIModel } });
      setInput('');
      setTextareaHeight(COMPOSER_MIN_HEIGHT);
    },
    [isBusy, selectedA2UIModel, selectedModel, selectedReasoning, sendMessage, setInput],
  );

  useEffect(() => {
    const handleCanvasUserAction = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail : undefined;
      if (isCanvasUserAction(detail)) submitCanvasUserAction(detail);
    };
    window.addEventListener('a2ui:canvas-user-action', handleCanvasUserAction);
    return () => window.removeEventListener('a2ui:canvas-user-action', handleCanvasUserAction);
  }, [submitCanvasUserAction]);

  const footerSelectors = (
    <div className="flex min-w-0 flex-wrap items-center gap-2 [container-type:inline-size]">
      <style>{morphSelectResponsiveStyle}</style>
      <MorphSelect label="A2UI Model" icon={Bot} value={selectedA2UIModel} options={a2uiModelOptions} onChange={setSelectedA2UIModel} />
      <MorphSelect label="Chat Model" icon={MessageCircle} value={selectedModel} options={chatModelOptions} onChange={setSelectedModel} />
      <MorphSelect label="Reasoning" icon={Brain} value={selectedReasoning} options={reasoningOptions} onChange={setSelectedReasoning} />
    </div>
  );

  return (
    <main className="flex min-h-0 flex-1 flex-col rounded-[10px] border border-[#e7ddd2] bg-white text-[#2a2a29] shadow-[0_1px_2px_rgba(32,24,12,0.04)] md:my-1" aria-label="A2UI chatbot">
      {hasUserMessages ? (
        <>
          <section
            ref={conversationRef}
            className="flex min-h-0 flex-1 flex-col items-center overflow-auto px-3 pb-2 pt-4 [scrollbar-width:thin] md:px-4 md:pb-3 md:pt-6"
            style={{
              WebkitMaskImage:
                conversationFade.top || conversationFade.bottom
                  ? `linear-gradient(180deg, ${conversationFade.top ? 'transparent 0, #000 28px' : '#000 0'}, #000 calc(100% - 28px), ${conversationFade.bottom ? 'transparent 100%' : '#000 100%'})`
                  : undefined,
              maskImage:
                conversationFade.top || conversationFade.bottom
                  ? `linear-gradient(180deg, ${conversationFade.top ? 'transparent 0, #000 28px' : '#000 0'}, #000 calc(100% - 28px), ${conversationFade.bottom ? 'transparent 100%' : '#000 100%'})`
                  : undefined,
            }}
          >
            {visibleMessages.map((message) => {
              const active = message.role === 'assistant' && activeThought?.assistantMessageId === message.id;
              const finishedSeconds = thoughtDurations[message.id];
              const thoughtActivity = active && !shouldShowPending ? { active: true, seconds: getElapsedSeconds(activeThought.startedAt, now) } : finishedSeconds === undefined ? undefined : { active: false, seconds: finishedSeconds };
              return <ChatMessage key={message.id} message={message} isStreaming={status === 'streaming' && message.role === 'assistant' && lastVisibleMessage?.id === message.id} thoughtActivity={thoughtActivity} />;
            })}
            {shouldShowPending ? <div className="flex w-full justify-start py-2 md:w-[min(768px,100%)] md:py-3">{pendingThoughtActivity ? <ThoughtTimerActivity activity={pendingThoughtActivity} /> : null}</div> : null}
            <div ref={bottomRef} />
          </section>
          {error ? <div className="mx-auto mb-3 w-full rounded-2xl bg-[#ff5a70]/10 px-4 py-2 text-[13px] text-[#b4233a] md:w-[min(768px,100%)]">{error instanceof Error ? error.message : 'Chat request failed.'}</div> : null}
          <div className="mx-auto w-full px-2 pb-2 md:w-[min(768px,100%)] md:px-0 md:pb-3">
            <ChatComposer
              input={input}
              setInput={setInput}
              inputHistory={inputHistory}
              textareaHeight={textareaHeight}
              setTextareaHeight={setTextareaHeight}
              isBusy={isBusy}
              onSubmit={submitText}
              onStop={stop}
              footer={footerSelectors}
            />
          </div>
        </>
      ) : (
        <section className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-5 md:px-4 md:py-6">
          {error ? <div className="mb-4 w-full rounded-2xl bg-[#ff5a70]/10 px-4 py-2 text-[13px] text-[#b4233a] md:w-[min(768px,100%)]">{error instanceof Error ? error.message : 'Chat request failed.'}</div> : null}
          <div className="grid justify-items-center gap-4 text-center">
            <div>
              <h1 className="m-0 font-[var(--font-display)] text-[26px] font-normal leading-[1.08] tracking-[-0.02em] text-[#2f2d2b] md:text-[32px] desktop:text-[42px]">How can I help with A2UI?</h1>
              <p className="mt-3 font-serif text-[14px] font-normal leading-[1.35] text-[#2f2d2b] md:text-[16px] desktop:text-[20px]">Sketch states, critique payloads, or turn a component idea into a sharper interaction.</p>
            </div>
          </div>
          <div className="mt-6 w-full md:mt-8 md:w-[min(768px,100%)]">
            <ChatComposer
              input={input}
              setInput={setInput}
              inputHistory={inputHistory}
              textareaHeight={textareaHeight}
              setTextareaHeight={setTextareaHeight}
              isBusy={isBusy}
              onSubmit={submitText}
              onStop={stop}
              footer={footerSelectors}
            />
            <div className="mt-4 px-2 opacity-100 transition duration-150 md:px-6">
              {starterPrompts.map((prompt, index) => {
                const StarterPromptIcon = starterPromptIcons[index];
                return (
                  <button
                    key={prompt}
                    type="button"
                    disabled={isBusy}
                    onClick={() => submitText(prompt)}
                    className={
                      index === 0
                        ? 'flex w-full items-center gap-2 px-1 py-3.5 text-left text-[14px] leading-5 text-[#595856] transition hover:text-[#2a2a29] active:scale-[0.995] disabled:cursor-not-allowed disabled:opacity-50'
                        : 'relative flex w-full items-center gap-2 px-1 py-3.5 text-left text-[14px] leading-5 text-[#595856] transition before:absolute before:left-[calc(1rem+0.5rem+0.25rem)] before:top-0 before:h-px before:w-1/2 before:bg-[#2a2a29]/[0.08] hover:text-[#2a2a29] active:scale-[0.995] disabled:cursor-not-allowed disabled:opacity-50'
                    }
                  >
                    <StarterPromptIcon className="h-4 w-4 shrink-0 text-[#EE5C2A]" strokeWidth={2} />
                    <span>{prompt}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
